import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Form, Button, Table, Alert, Spinner, Badge } from 'react-bootstrap';
import { FaUpload, FaRocket, FaCheckCircle, FaExclamationTriangle, FaFileAlt, FaImage, FaUsers, FaPlus, FaDownload, FaArrowRight, FaArrowLeft } from 'react-icons/fa';
import Layout from './Layout';
import SdAudienceCampaignAPI from '../services/SdAudienceCampaignAPI';
import '../assets/css/SdAudienceCampaigns.css';

const SdAudienceCampaigns = () => {
    // Files
    const [ownProductsFile, setOwnProductsFile] = useState(null);
    const [audiencesFile, setAudiencesFile] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);

    // Processed Data
    const [ownProducts, setOwnProducts] = useState([]);
    const [audiences, setAudiences] = useState([]);
    const [selectedOwnProducts, setSelectedOwnProducts] = useState([]);
    const [selectedAudiences, setSelectedAudiences] = useState([]);
    const [presetAudiences, setPresetAudiences] = useState([]);

    // Campaign Configuration
    const [campaignName, setCampaignName] = useState('');
    const [headline, setHeadline] = useState('');
    const [dailyBudget, setDailyBudget] = useState(15);
    const [defaultBid, setDefaultBid] = useState(1.2);
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState('');
    const [bidOptimization, setBidOptimization] = useState('clicks');
    const [campaignState, setCampaignState] = useState('paused');

    // UI States
    const [loading, setLoading] = useState(false);
    const [uploadingFiles, setUploadingFiles] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [step, setStep] = useState(1); // 1: Upload/Presets, 2: Select, 3: Configure, 4: Results

    const getProfileId = () => {
        return localStorage.getItem('selectedAccountValue') || null;
    };

    useEffect(() => {
        loadPresets();
    }, []);

    const loadPresets = async () => {
        try {
            const resp = await SdAudienceCampaignAPI.getPresets();
            if (resp && resp.data) {
                setPresetAudiences(resp.data);
            }
        } catch (e) {
            console.error('Error loading audience presets:', e);
        }
    };

    const handleOwnProductsChange = (e) => {
        const file = e.target.files[0];
        if (file && file.name.endsWith('.csv')) {
            setOwnProductsFile(file);
        }
    };

    const handleAudiencesChange = (e) => {
        const file = e.target.files[0];
        if (file && file.name.endsWith('.csv')) {
            setAudiencesFile(file);
        }
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith('image/')) {
            setLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setLogoPreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleUploadFiles = async () => {
        if (!ownProductsFile && !audiencesFile && selectedAudiences.length === 0) {
            setError('Please upload at least the Own Products CSV file or pick audience presets.');
            return;
        }

        setUploadingFiles(true);
        setError(null);

        try {
            const response = await SdAudienceCampaignAPI.uploadFiles(ownProductsFile, audiencesFile, logoFile);
            if (response.success) {
                const prods = response.data.ownProducts || [];
                let auds = response.data.audiences || [];

                // If no audience CSV uploaded, but presets were selected, use presets
                if (auds.length === 0 && selectedAudiences.length > 0) {
                    auds = presetAudiences.filter(p => selectedAudiences.includes(p.id));
                }

                setOwnProducts(prods);
                setAudiences(auds);
                setSelectedOwnProducts(prods.map(p => p.asin));
                setSelectedAudiences(auds.map(a => a.id));

                if (!campaignName && prods.length > 0) {
                    setCampaignName(`SD_AUDIENCE_${prods[0].asin}_${new Date().toISOString().slice(0, 10)}`);
                }

                setStep(2);
            } else {
                setError(response.error || 'Error processing files');
            }
        } catch (err) {
            setError(err.message || 'Error uploading files');
        } finally {
            setUploadingFiles(false);
        }
    };

    const togglePreset = (preset) => {
        if (selectedAudiences.includes(preset.id)) {
            setSelectedAudiences(prev => prev.filter(id => id !== preset.id));
        } else {
            setSelectedAudiences(prev => [...prev, preset.id]);
        }
    };

    const toggleOwnProduct = (asin) => {
        setSelectedOwnProducts(prev =>
            prev.includes(asin) ? prev.filter(a => a !== asin) : [...prev, asin]
        );
    };

    const toggleAudience = (id) => {
        setSelectedAudiences(prev =>
            prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
        );
    };

    const handleCreateCampaign = async () => {
        const profileId = getProfileId();
        if (!profileId) {
            setError('Please select an active Amazon Ads account in the top bar before proceeding.');
            return;
        }

        if (!campaignName.trim()) {
            setError('Campaign name is required');
            return;
        }

        if (selectedOwnProducts.length === 0) {
            setError('Please select at least one product to advertise');
            return;
        }

        if (selectedAudiences.length === 0) {
            setError('Please select at least one target audience');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const targetAudienceList = audiences.filter(a => selectedAudiences.includes(a.id));

            const response = await SdAudienceCampaignAPI.createCampaign({
                profileId,
                campaignName,
                dailyBudget,
                startDate,
                endDate: endDate || undefined,
                headline,
                ownProducts: selectedOwnProducts,
                audiences: targetAudienceList,
                bidOptimization,
                defaultBid,
                state: campaignState,
                logo: logoFile
            });

            setResult(response);
            setStep(4);
        } catch (err) {
            setError(err.message || 'Error creating audience campaign');
        } finally {
            setLoading(false);
        }
    };

    const downloadSampleCSV = (type) => {
        let content = '';
        let filename = '';
        if (type === 'products') {
            content = 'ASIN,SKU,Price,Title\nB08N5WRWNW,MY-PROD-01,29.99,Wireless Noise Cancelling Headphones\nB09V3HBK6L,MY-PROD-02,49.99,Smart Fitness Watch';
            filename = 'sample_own_products.csv';
        } else {
            content = 'AudienceId,AudienceName,Category,Bid\n323573216892544,In-Market: Home & Kitchen Enthusiasts,In-Market,1.20\n213876356784532,In-Market: Electronics Buyers,In-Market,1.50\n453218765432987,Lifestyle: Fitness & Wellness,Lifestyle,1.00';
            filename = 'sample_audiences.csv';
        }
        const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const resetForm = () => {
        setStep(1);
        setOwnProductsFile(null);
        setAudiencesFile(null);
        setLogoFile(null);
        setLogoPreview(null);
        setOwnProducts([]);
        setAudiences([]);
        setSelectedOwnProducts([]);
        setSelectedAudiences([]);
        setCampaignName('');
        setResult(null);
        setError(null);
    };

    return (
        <Layout>
            <div className="sd-audience-campaigns-page">
                {/* Header Banner */}
                <div className="sd-header-banner">
                    <div>
                        <div className="sd-header-title">
                            <FaUsers className="text-primary" /> Sponsored Display — Audience Campaigns
                        </div>
                        <p className="sd-header-subtitle">
                            Target high-intent shopper audiences (In-Market, Lifestyle, Interests) across and outside Amazon.
                        </p>
                    </div>
                    <div className="d-flex gap-2">
                        <Button variant="outline-light" size="sm" onClick={() => downloadSampleCSV('products')}>
                            <FaDownload className="me-1" /> Products Template
                        </Button>
                        <Button variant="outline-light" size="sm" onClick={() => downloadSampleCSV('audiences')}>
                            <FaDownload className="me-1" /> Audiences Template
                        </Button>
                    </div>
                </div>

                {/* Progress Steps */}
                <div className="progress-steps">
                    <div className={`step ${step === 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`} onClick={() => step > 1 && setStep(1)}>
                        <div className="step-number">{step > 1 ? <FaCheckCircle /> : '1'}</div>
                        <span className="step-label">Upload & Presets</span>
                    </div>
                    <div className={`step ${step === 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`} onClick={() => step > 2 && setStep(2)}>
                        <div className="step-number">{step > 2 ? <FaCheckCircle /> : '2'}</div>
                        <span className="step-label">Select Items</span>
                    </div>
                    <div className={`step ${step === 3 ? 'active' : ''} ${step > 3 ? 'completed' : ''}`} onClick={() => step > 3 && setStep(3)}>
                        <div className="step-number">{step > 3 ? <FaCheckCircle /> : '3'}</div>
                        <span className="step-label">Campaign Setup</span>
                    </div>
                    <div className={`step ${step === 4 ? 'active' : ''}`}>
                        <div className="step-number">4</div>
                        <span className="step-label">Results</span>
                    </div>
                </div>

                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError(null)} className="d-flex align-items-center mb-4">
                        <FaExclamationTriangle className="me-2 fs-5" />
                        <div>{error}</div>
                    </Alert>
                )}

                {/* STEP 1: Upload & Presets */}
                {step === 1 && (
                    <div>
                        <Row className="g-4 mb-4">
                            {/* Own Products Upload */}
                            <Col md={4}>
                                <Card className="upload-card">
                                    <Card.Body>
                                        <div className="card-title">
                                            <FaFileAlt className="text-primary" /> Promoted Products (CSV)
                                        </div>
                                        <label className="upload-area w-100">
                                            <input type="file" accept=".csv" onChange={handleOwnProductsChange} style={{ display: 'none' }} />
                                            <FaUpload />
                                            <div>{ownProductsFile ? ownProductsFile.name : 'Drag & drop or click to upload CSV'}</div>
                                            <small className="text-muted mt-1">Columns: ASIN, SKU, Title, Price</small>
                                        </label>
                                        {ownProductsFile && (
                                            <div className="file-info-badge">
                                                <FaCheckCircle className="text-success" /> {ownProductsFile.name} ({(ownProductsFile.size / 1024).toFixed(1)} KB)
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Target Audiences Upload */}
                            <Col md={4}>
                                <Card className="upload-card">
                                    <Card.Body>
                                        <div className="card-title">
                                            <FaUsers className="text-info" /> Target Audiences (CSV)
                                        </div>
                                        <label className="upload-area w-100">
                                            <input type="file" accept=".csv" onChange={handleAudiencesChange} style={{ display: 'none' }} />
                                            <FaUpload />
                                            <div>{audiencesFile ? audiencesFile.name : 'Upload custom audiences CSV (optional)'}</div>
                                            <small className="text-muted mt-1">Columns: AudienceId, AudienceName, Category, Bid</small>
                                        </label>
                                        {audiencesFile && (
                                            <div className="file-info-badge">
                                                <FaCheckCircle className="text-success" /> {audiencesFile.name} ({(audiencesFile.size / 1024).toFixed(1)} KB)
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Brand Logo Upload */}
                            <Col md={4}>
                                <Card className="upload-card">
                                    <Card.Body>
                                        <div className="card-title">
                                            <FaImage className="text-warning" /> Brand Logo (Optional)
                                        </div>
                                        <label className="upload-area w-100">
                                            <input type="file" accept="image/*" onChange={handleLogoChange} style={{ display: 'none' }} />
                                            {logoPreview ? (
                                                <img src={logoPreview} alt="Logo preview" style={{ maxHeight: '60px', maxWidth: '100%', objectFit: 'contain' }} />
                                            ) : (
                                                <>
                                                    <FaImage />
                                                    <div>Click to upload brand logo image</div>
                                                    <small className="text-muted mt-1">PNG, JPG recommended 600x100px</small>
                                                </>
                                            )}
                                        </label>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Quick Audience Presets */}
                        <Card className="config-card mb-4">
                            <Card.Header>
                                <FaUsers className="me-2 text-primary" /> Recommended Amazon Audience Segments
                            </Card.Header>
                            <Card.Body>
                                <p className="text-muted small mb-3">
                                    You can quickly select pre-built Amazon In-Market and Lifestyle audiences below, or upload your own CSV file above.
                                </p>
                                <div className="d-flex flex-wrap">
                                    {presetAudiences.map(p => {
                                        const isSelected = selectedAudiences.includes(p.id);
                                        return (
                                            <div
                                                key={p.id}
                                                className={`audience-preset-badge ${isSelected ? 'selected' : ''}`}
                                                onClick={() => togglePreset(p)}
                                            >
                                                {isSelected ? <FaCheckCircle /> : <FaPlus />}
                                                <span>{p.name}</span>
                                                <Badge bg="dark" className="ms-1">{p.category}</Badge>
                                            </div>
                                        );
                                    })}
                                </div>
                            </Card.Body>
                        </Card>

                        <div className="d-flex justify-content-end">
                            <Button className="btn-primary-sd" onClick={handleUploadFiles} disabled={uploadingFiles || (!ownProductsFile && selectedAudiences.length === 0)}>
                                {uploadingFiles ? <Spinner size="sm" animation="border" className="me-2" /> : <FaArrowRight className="me-2" />}
                                Continue to Selection
                            </Button>
                        </div>
                    </div>
                )}

                {/* STEP 2: Selection & Review */}
                {step === 2 && (
                    <div>
                        <Row className="g-4 mb-4">
                            {/* Promoted Products Table */}
                            <Col md={6}>
                                <Card className="product-card">
                                    <Card.Header className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <strong>Promoted Products ({selectedOwnProducts.length}/{ownProducts.length})</strong>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <Button size="sm" variant="outline-light" onClick={() => setSelectedOwnProducts(ownProducts.map(p => p.asin))}>Select All</Button>
                                            <Button size="sm" variant="outline-light" onClick={() => setSelectedOwnProducts([])}>Clear</Button>
                                        </div>
                                    </Card.Header>
                                    <div className="product-list">
                                        <Table responsive hover size="sm">
                                            <thead>
                                                <tr>
                                                    <th width="40"></th>
                                                    <th>ASIN</th>
                                                    <th>Title</th>
                                                    <th>Price</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {ownProducts.map(prod => (
                                                    <tr key={prod.asin} className={selectedOwnProducts.includes(prod.asin) ? 'table-active' : ''} onClick={() => toggleOwnProduct(prod.asin)} style={{ cursor: 'pointer' }}>
                                                        <td>
                                                            <Form.Check type="checkbox" checked={selectedOwnProducts.includes(prod.asin)} onChange={() => {}} />
                                                        </td>
                                                        <td className="font-monospace text-info">{prod.asin}</td>
                                                        <td className="text-truncate" style={{ maxWidth: '200px' }}>{prod.title || prod.sku || '—'}</td>
                                                        <td>{prod.price ? `$${prod.price.toFixed(2)}` : '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Card>
                            </Col>

                            {/* Target Audiences Table */}
                            <Col md={6}>
                                <Card className="product-card">
                                    <Card.Header className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <strong>Target Audiences ({selectedAudiences.length}/{audiences.length})</strong>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <Button size="sm" variant="outline-light" onClick={() => setSelectedAudiences(audiences.map(a => a.id))}>Select All</Button>
                                            <Button size="sm" variant="outline-light" onClick={() => setSelectedAudiences([])}>Clear</Button>
                                        </div>
                                    </Card.Header>
                                    <div className="product-list">
                                        <Table responsive hover size="sm">
                                            <thead>
                                                <tr>
                                                    <th width="40"></th>
                                                    <th>Audience ID</th>
                                                    <th>Name / Category</th>
                                                    <th>Suggested Bid</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {audiences.map(aud => (
                                                    <tr key={aud.id} className={selectedAudiences.includes(aud.id) ? 'table-active' : ''} onClick={() => toggleAudience(aud.id)} style={{ cursor: 'pointer' }}>
                                                        <td>
                                                            <Form.Check type="checkbox" checked={selectedAudiences.includes(aud.id)} onChange={() => {}} />
                                                        </td>
                                                        <td className="font-monospace text-warning">{aud.id}</td>
                                                        <td>
                                                            <div>{aud.name}</div>
                                                            <Badge bg="secondary" style={{ fontSize: '0.7rem' }}>{aud.category}</Badge>
                                                        </td>
                                                        <td>${(aud.bid || defaultBid).toFixed(2)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Card>
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-between">
                            <Button variant="secondary" onClick={() => setStep(1)}>
                                <FaArrowLeft className="me-2" /> Back
                            </Button>
                            <Button className="btn-primary-sd" onClick={() => setStep(3)} disabled={selectedOwnProducts.length === 0 || selectedAudiences.length === 0}>
                                Configure Campaign <FaArrowRight className="ms-2" />
                            </Button>
                        </div>
                    </div>
                )}

                {/* STEP 3: Campaign Setup */}
                {step === 3 && (
                    <div>
                        <Row className="g-4 mb-4">
                            <Col md={7}>
                                <Card className="config-card">
                                    <Card.Header>
                                        <FaRocket className="me-2 text-primary" /> Campaign Parameters
                                    </Card.Header>
                                    <Card.Body>
                                        <Form>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Campaign Name *</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={campaignName}
                                                    onChange={(e) => setCampaignName(e.target.value)}
                                                    placeholder="e.g. SD_Audience_Headphones_Q3"
                                                />
                                            </Form.Group>

                                            <Row className="mb-3">
                                                <Col md={6}>
                                                    <Form.Group>
                                                        <Form.Label>Daily Budget ($) *</Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            step="1"
                                                            min="1"
                                                            value={dailyBudget}
                                                            onChange={(e) => setDailyBudget(e.target.value)}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group>
                                                        <Form.Label>Default Bid ($) *</Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            step="0.05"
                                                            min="0.02"
                                                            value={defaultBid}
                                                            onChange={(e) => setDefaultBid(e.target.value)}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>

                                            <Row className="mb-3">
                                                <Col md={6}>
                                                    <Form.Group>
                                                        <Form.Label>Start Date</Form.Label>
                                                        <Form.Control
                                                            type="date"
                                                            value={startDate}
                                                            onChange={(e) => setStartDate(e.target.value)}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group>
                                                        <Form.Label>End Date (Optional)</Form.Label>
                                                        <Form.Control
                                                            type="date"
                                                            value={endDate}
                                                            onChange={(e) => setEndDate(e.target.value)}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>

                                            <Row className="mb-3">
                                                <Col md={6}>
                                                    <Form.Group>
                                                        <Form.Label>Bid Optimization</Form.Label>
                                                        <Form.Select
                                                            value={bidOptimization}
                                                            onChange={(e) => setBidOptimization(e.target.value)}
                                                        >
                                                            <option value="clicks">Optimize for Page Visits (Clicks)</option>
                                                            <option value="conversions">Optimize for Conversions</option>
                                                            <option value="reach">Optimize for Viewable Impressions</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group>
                                                        <Form.Label>Initial State</Form.Label>
                                                        <Form.Select
                                                            value={campaignState}
                                                            onChange={(e) => setCampaignState(e.target.value)}
                                                        >
                                                            <option value="paused">Paused (Recommended for safety)</option>
                                                            <option value="enabled">Enabled (Active immediately)</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                            </Row>

                                            <Form.Group className="mb-3">
                                                <Form.Label>Ad Headline (Optional)</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={headline}
                                                    onChange={(e) => setHeadline(e.target.value)}
                                                    placeholder="Catchy headline for your Sponsored Display ad"
                                                />
                                            </Form.Group>
                                        </Form>
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col md={5}>
                                <Card className="config-card">
                                    <Card.Header>
                                        <strong>Summary Review</strong>
                                    </Card.Header>
                                    <Card.Body>
                                        <ul className="summary-list">
                                            <li>
                                                <span className="text-muted">Targeting Tactic:</span>
                                                <Badge bg="primary">Sponsored Display (Audiences - T00020)</Badge>
                                            </li>
                                            <li>
                                                <span className="text-muted">Promoted Products:</span>
                                                <Badge bg="info">{selectedOwnProducts.length} ASINs</Badge>
                                            </li>
                                            <li>
                                                <span className="text-muted">Target Audiences:</span>
                                                <Badge bg="warning" text="dark">{selectedAudiences.length} Segments</Badge>
                                            </li>
                                            <li>
                                                <span className="text-muted">Daily Budget:</span>
                                                <strong className="text-success">${parseFloat(dailyBudget).toFixed(2)}/day</strong>
                                            </li>
                                            <li>
                                                <span className="text-muted">Optimization:</span>
                                                <span className="text-capitalize">{bidOptimization}</span>
                                            </li>
                                            <li>
                                                <span className="text-muted">Logo Included:</span>
                                                <span>{logoFile ? <Badge bg="success">Yes</Badge> : <Badge bg="secondary">No</Badge>}</span>
                                            </li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-between">
                            <Button variant="secondary" onClick={() => setStep(2)}>
                                <FaArrowLeft className="me-2" /> Back
                            </Button>
                            <Button className="btn-primary-sd" onClick={handleCreateCampaign} disabled={loading}>
                                {loading ? <Spinner size="sm" animation="border" className="me-2" /> : <FaRocket className="me-2" />}
                                Launch Audience Campaign
                            </Button>
                        </div>
                    </div>
                )}

                {/* STEP 4: Execution Results */}
                {step === 4 && result && (
                    <Card className="result-card p-4">
                        <div className="text-center mb-4">
                            <FaCheckCircle className="text-success display-3 mb-3" />
                            <h3>Sponsored Display Audience Campaign Created!</h3>
                            <p className="text-muted">Your campaign has been generated and dispatched to Amazon Advertising API.</p>
                        </div>

                        <Row className="g-3 mb-4">
                            <Col md={4}>
                                <div className="result-stat-box">
                                    <small className="text-muted d-block fw-bold text-uppercase" style={{ fontSize: '0.75rem' }}>Campaign ID</small>
                                    <strong className="text-primary fs-5">{result.data?.campaign?.campaignId || 'Generated'}</strong>
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="result-stat-box">
                                    <small className="text-muted d-block fw-bold text-uppercase" style={{ fontSize: '0.75rem' }}>Ad Group ID</small>
                                    <strong className="text-secondary fs-5">{result.data?.adGroup?.adGroupId || 'Generated'}</strong>
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="result-stat-box">
                                    <small className="text-muted d-block fw-bold text-uppercase" style={{ fontSize: '0.75rem' }}>Target Audiences</small>
                                    <strong className="text-success fs-5">{selectedAudiences.length} Clauses</strong>
                                </div>
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-center gap-3">
                            <Button variant="outline-dark" onClick={resetForm}>
                                Create Another Campaign
                            </Button>
                        </div>
                    </Card>
                )}
            </div>
        </Layout>
    );
};

export default SdAudienceCampaigns;
